/**
 * closure — luật HIỂN THỊ của bước KHÉP ĐƠN (QĐ 0292, CEO chốt 04/10/2026).
 *
 * Máy chủ tính bảng kiểm (`flags[id].closure` của GET /orders/attention-flags) và
 * là bên DUY NHẤT quyết định đơn khép hay chưa. Tệp này chỉ đổi bảng kiểm thành
 * chữ: không tự tính lại điều kiện nào, không có ngưỡng riêng.
 *
 * Tách khỏi phần vẽ để bài kiểm soi được chữ mà không phải dựng DOM, và để thẻ
 * khép đơn, thanh hành trình và danh sách đơn nói CÙNG một câu về cùng một đơn.
 */

import { format } from "date-fns";

import { acceptChannelLabel } from "@/features/cskh/acceptChannels";
import { stepLabel } from "@/features/cskh/printOrderShared";
import type { components } from "@/lib/api/generated/schema";
import { formatVND } from "@/lib/format";

import type { OrderTab } from "./orderTabs";

export type OrderClosure = components["schemas"]["OrderClosure"];

/** Hạn bước ⑤ của sổ CSKH (BUSINESS_RULES §4) — chỉ để IN ra "(hẹn 2 ngày)". */
export const ACCEPT_SLA_DAYS = 2;

/**
 * Đơn ĐÃ KHÉP chỉ khi có mốc khép VÀ không còn thiếu việc nào. Mốc cũ có thể
 * còn sót vài phút sau khi một việc vỡ (khách xin sửa lại) — lúc đó phải vẽ như
 * đơn chưa khép, không in "đã khép" lên một đơn đang thiếu việc.
 */
export function isClosed(c: OrderClosure): boolean {
  return !!c.closedAt && c.missing.length === 0;
}

/** Đủ cả ba việc nhưng máy chưa kịp đóng dấu (lượt quét 10 phút một lần). */
export function isAwaitingStamp(c: OrderClosure): boolean {
  return !c.closedAt && c.missing.length === 0;
}

const fmt = (iso: string | undefined, pattern: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : format(d, pattern);
};

/**
 * Đỏ khi còn thiếu việc VÀ đã quá hạn chờ — cùng mốc máy đi tuần bắt đầu réo,
 * để màn hình và bản tin sáng nói cùng một điều về cùng một đơn.
 */
export function isClosureLate(c: OrderClosure): boolean {
  return c.missing.length > 0 && c.days > ACCEPT_SLA_DAYS;
}

/** Nhãn tóm tắt cạnh tên khách ở chi tiết đơn. */
export function closureSummary(c: OrderClosure): { tone: "ok" | "warn"; label: string } {
  if (isClosed(c)) return { tone: "ok", label: "Đã khép" };
  if (isAwaitingStamp(c)) return { tone: "ok", label: "Đủ điều kiện khép" };
  return { tone: "warn", label: `Chưa khép · còn ${c.missing.length} việc` };
}

/** Dòng tiêu đề của thẻ khép đơn. */
export function closureHeadline(c: OrderClosure): string {
  if (isClosed(c)) return `Đơn đã khép · ${fmt(c.closedAt, "dd/MM/yyyy HH:mm")}`;
  if (isAwaitingStamp(c)) return "Đã đủ điều kiện — máy sẽ khép đơn trong ít phút";
  return `Còn ${c.missing.length} việc để khép đơn này`;
}

/** Dòng chữ dưới ô "Khép đơn" của thanh hành trình. */
export function closureStepText(c: OrderClosure): string {
  if (isClosed(c)) return `${fmt(c.closedAt, "dd/MM HH:mm")} · máy khép`;
  if (isAwaitingStamp(c)) return "đủ điều kiện · chờ máy khép";
  return c.days <= 0 ? "chưa khép · mới giao hôm nay" : `chưa khép · đã ${c.days} ngày`;
}

/**
 * Tên bước của đơn in chậm nhất. Đi qua stepLabel của màn Đơn in — mọi chỗ hiển
 * thị bước dùng chung một bảng nhãn, không chép bảng thứ hai ở đây.
 */
export function printStepLabel(step: string | undefined): string {
  return step ? stepLabel(step) : "";
}

export type ClosureItemState = "done" | "waiting" | "missing" | "skipped" | "optional";

export interface ClosureItem {
  key: "received" | "paid" | "print" | "rated";
  state: ClosureItemState;
  title: string;
  detail: string;
  /** "Việc của: …" — chỉ có ở việc CHƯA xong. */
  owner?: string;
  /** Thẻ của chi tiết đơn nơi xử lý việc này, và chữ trên nút dẫn tới đó. */
  action?: { tab: OrderTab; label: string };
}

function receivedItem(c: OrderClosure): ClosureItem {
  const r = c.received;
  if (r.done) {
    const at = fmt(r.at, "dd/MM");
    let detail = "Khách tự bấm trên Cổng";
    if (r.source === "counter") detail = "Ảnh thẻ trao tại quầy";
    else if (r.source === "staff") {
      const via = r.channel ? ` qua ${acceptChannelLabel(r.channel)}` : "";
      detail = `Xác nhận hộ${via}${r.byName ? ` · ${r.byName}` : ""}`;
    }
    return { key: "received", state: "done", title: "Khách đã nhận đủ ảnh", detail: at ? `${detail} · ${at}` : detail };
  }
  const sent = fmt(c.deliveredAt, "dd/MM");
  const waited = c.days <= 0 ? "mới gửi hôm nay" : `khách chưa trả lời ${c.days} ngày`;
  return {
    key: "received",
    state: "waiting",
    title: "Khách xác nhận đã nhận đủ ảnh",
    detail: `Đã gửi ảnh ${sent} · ${waited} (hẹn ${ACCEPT_SLA_DAYS} ngày)`,
    owner: "CSKH · được nhắc mỗi sáng tới khi xong",
    action: { tab: "cskh", label: "Mở sổ CSKH" },
  };
}

function paidItem(c: OrderClosure): ClosureItem {
  if (c.paid.done) {
    return { key: "paid", state: "done", title: "Đã thu đủ tiền", detail: "Không còn khoản nào phải thu" };
  }
  const owner = "Lễ tân · có trong bản tin sáng của lễ tân và trưởng cửa hàng";
  // Người không được xem tiền của đơn (QĐ 0132): máy chủ đã che con số. Vẫn nói
  // việc còn thiếu, không in "còn 0 đ" và không có nút sang thẻ họ không có.
  if (c.paid.amountHidden) {
    return { key: "paid", state: "missing", title: "Thu đủ tiền", detail: "Còn tiền chưa thu", owner };
  }
  return {
    key: "paid",
    state: "missing",
    title: "Thu đủ tiền",
    detail: `Còn ${formatVND(c.paid.remaining)} chưa thu`,
    owner,
    action: { tab: "payment", label: "Thu tiền" },
  };
}

function printItem(c: OrderClosure): ClosureItem {
  const p = c.print;
  if (p.total === 0) {
    return { key: "print", state: "skipped", title: "Hàng in", detail: "Đơn này không có hàng in" };
  }
  if (p.done) {
    return { key: "print", state: "done", title: "Hàng in đã tới tay khách", detail: `${p.total} đơn in đã giao xong` };
  }
  const step = printStepLabel(p.step);
  return {
    key: "print",
    state: "waiting",
    title: "Hàng in tới tay khách",
    detail: `${p.open}/${p.total} đơn in chưa trao${step ? ` · chậm nhất đang ở bước ${step}` : ""}`,
    owner: "Lễ tân cơ sở nhận hàng",
    action: { tab: "surcharge", label: "Mở đơn in" },
  };
}

function ratedItem(c: OrderClosure): ClosureItem {
  return c.rated
    ? { key: "rated", state: "done", title: "Khách đã chấm điểm ê-kíp", detail: "Không bắt buộc" }
    : {
        key: "rated",
        state: "optional",
        title: "Khách chấm điểm ê-kíp",
        detail: "Chưa chấm · không bắt buộc, không chặn việc khép đơn",
      };
}

/** Bốn dòng của thẻ khép đơn, thứ tự cố định: ba việc bắt buộc rồi tới chấm điểm. */
export function closureItems(c: OrderClosure): ClosureItem[] {
  return [receivedItem(c), paidItem(c), printItem(c), ratedItem(c)];
}

export interface ClosureChip {
  key: string;
  tone: "warn" | "info";
  label: string;
}

/**
 * Nhãn "còn thiếu" trên DANH SÁCH đơn. Chỉ hai việc: chờ khách nhận và hàng in.
 * Nợ CỐ Ý không có ở đây — nhãn đỏ "Còn nợ …" sẵn có đã nói việc đó, in thêm là
 * một đơn hai nhãn cùng một nghĩa.
 */
export function closureChips(c: OrderClosure | undefined): ClosureChip[] {
  if (!c || isClosed(c)) return [];
  const out: ClosureChip[] = [];
  if (!c.received.done) {
    out.push({
      key: "received",
      tone: "warn",
      label: c.days > 0 ? `Chờ khách nhận · ${c.days} ngày` : "Chờ khách nhận",
    });
  }
  if (!c.print.done) {
    const step = printStepLabel(c.print.step);
    out.push({ key: "print", tone: "info", label: step ? `Hàng in: ${step}` : "Hàng in chưa trao" });
  }
  return out;
}
